<template>
  <ErrorHandler
    v-if="showProspectCallbackError || null"
    :prospect-callback-error-msg="prospectCallbackErrorMsg"
    :is-positive="isPositiveNotification"
    @reset-show-prospect-callback-error="resetShowProspectCallbackError"
  />

  <!-- Create account Modal -->
  <div
    v-if="isShowResetModal"
    id="passwordResetModal"
    ref="passwordResetModal"
    class="modal fade password-reset-modal tw-block show"
    aria-labelledby="passwordResetModalLabel"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <button
            type="button"
            class="close"
            aria-label="Close"
            @click="onClickClose"
          >
            <img src="@/assets/img/close.svg" />
          </button>
        </div>
        <div class="modal-body">
          <div class="modal-body-sec1">
            {{ getUiText("app-user-forgot-password-heading-1") }}
          </div>
          <div class="form-group">
            <label for="id" class="control-label input-label-form">{{
              getUiText("app-user-forgot-password-email-heading-1")
            }}</label>
            <input
              id="email"
              ref="email"
              v-model="emailForgotPassword"
              data-cy="forgot-password-page-email-input"
              type="email"
              :class="!emailValid ? 'invalid-input' : ''"
              class="form-control form-control-lg form-input"
              tabindex="0"
              :placeholder="emailPlaceholder"
              @keydown.enter="submit"
              @blur="inputBlur('EMAIL', true, emailForgotPassword)"
              @input="
                (e) => {
                  loginErrorMsg = '';
                  inputBlur(
                    'EMAIL',
                    false,
                    (e.target as HTMLInputElement).value || '',
                  );
                }
              "
            />
            <div
              class="error-msg"
              :class="!emailValid ? 'mg-top-19' : ''"
              :v-if="!emailValid || null"
            >
              <span>{{ emailValidationMsg }}</span>
            </div>
          </div>
          <div class="help-text">
            {{ getUiText("app-user-forgot-password-email-help-text-1") }}
          </div>
          <div class="letsgo-btn">
            <button
              type="button"
              data-cy="forgot-password-page-submit-button"
              class="btn btn-primary letsgo-button"
              tabindex="3"
              :disabled="disableButton"
              @click="submit"
            >
              {{ getUiText("app-user-forgot-password-reset-button-text-1") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="isShowSecretModal || secret"
    id="passwordSecretModal"
    ref="passwordSecretModal"
    class="modal fade password-reset-modal tw-block show"
    aria-labelledby="passwordResetModalLabel"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <button
            type="button"
            class="close"
            aria-label="Close"
            @click="onCloseSecretModal"
          >
            <img src="@/assets/img/close.svg" />
          </button>
        </div>
        <div
          class="modal-body !tw-pr-[55px] !tw-pl-[71px] !tw-pt-[34px] !tw-pb-[56px]"
        >
          <div class="modal-body-sec1">
            {{ getUiText("user-reset-password-secret-title-1") }}
          </div>
          <div class="form-group !tw-mt-[32px] !tw-mb-[27px]">
            <label
              class="control-label input-label-form !tw-mt-[0px] !tw-mb-[22px]"
              >{{
                getUiText("user-reset-password-secret-description-1")
              }}</label
            >
            <div class="tw-flex tw-items-center">
              <div class="form-control form-control-lg form-input !tw-w-fit">
                {{ secret }}
              </div>
              <CopyInfo
                class="copy-info-container tw-bg-[#FFFFFF] tw-rounded-md !tw-ml-[15px]"
                :text="secret ? secret : ''"
                :style-icon-css="'tw-w-[18px] tw-h-[18px]'"
                :is-show-text="false"
              />
            </div>
          </div>
          <div class="letsgo-btn !tw-mt-0">
            <button
              type="button"
              class="btn btn-primary letsgo-button !tw-w-[137px]"
              tabindex="3"
              :disabled="disableButton"
              @click="onClickCompleteSecret"
            >
              {{ getUiText("app-profile-modal-update-email-address-button-1") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ErrorHandler from "@/components/common/exceptionHandler.vue";
import utilities from "@/utils/utilities";
import userService from "@/services/user/userService";
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import store from "@/store";
import CopyInfo from "../../common/copyInfo.vue";
import Utils from "@/utils/utilities";
import { load } from "recaptcha-v3";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import eventEmitter, { EmitterEvents } from "@/utils/eventEmitter";

const showProspectCallbackError = ref(false);
const prospectCallbackErrorMsg = ref("");
const email = ref("");
const emailForgotPassword = ref("");
const isPositiveNotification = ref(false);
const isLoading = ref(false);
const passwordResetModal: any = ref<null>(null);
const isShowResetModal: any = ref(true);
const isShowSecretModal: any = ref(false);
const passwordSecretModal: any = ref<null>(null);
const secret = ref("");
const emailPlaceholder = ref("");
const emailFieldFocus = ref(0);
const emailValid = ref(true);
const emailValidationMsg = ref("");
const disableButton = ref(true);

const router = useRouter();

const languageSelected = ref(store.state.LanguagePicker.languageSelected);
const defaultUiText = ref(store.state.LanguagePicker.defaultUiText);
const languageSelectedText = ref(
  store.state.LanguagePicker.languageSelectedText,
);
let loginErrorMsg = ref("");

const onResize = () => {
  emailForgotPassword.value && emailForgotPassword?.value?.focus();
};

const getUiText = (id, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const inputBlur = (inputIs: string, isBlur = false, val) => {
  if (isBlur) {
    emailFieldFocus.value++;
  }
  if (inputIs === "EMAIL" && emailFieldFocus.value > 0) {
    if (!Utils.IsEmailValid(isBlur ? emailForgotPassword.value : val)) {
      emailValid.value = false;
      emailValidationMsg.value = getUiText("app-prospect-error-invalid-email");
    } else {
      emailValid.value = true;
      emailValidationMsg.value = "";
    }
  }
  checkFormVals();
};

const checkFormVals = () => {
  if (
    emailForgotPassword.value === "" ||
    emailValid.value === false ||
    !Utils.IsEmailValid(emailForgotPassword.value)
  ) {
    disableButton.value = true;
  } else {
    disableButton.value = false;
  }
};

const resetShowProspectCallbackError = (props) => {
  const { isSuccess } = props;
  showProspectCallbackError.value = false;
  prospectCallbackErrorMsg.value = "";
  if (isSuccess) {
    isLoading.value = false;
    isShowResetModal.value = false;
    isShowSecretModal.value = true;
  }
};

const onCloseSecretModal = () => {
  isShowSecretModal.value = false;
  secret.value = "";
  isShowResetModal.value = true;
};

const onClickCompleteSecret = () => {
  isShowSecretModal.value = false;
  secret.value = "";
  isShowResetModal.value = true;
  router.push({ path: "/user/login" });
};

const submit = () => {
  if (emailForgotPassword.value === "") {
    return showErrorInfo({
      message: "Please enter your CyberSystem email address",
    });
  }

  if (utilities.IsEmailValid(emailForgotPassword.value) === false) {
    return showErrorInfo({
      message: "Please enter a valid emailForgotPassword address",
    });
  }
  isLoading.value = true;

  const data = {
    emailForgotPassword: emailForgotPassword.value,
  };
  return forgotPasswordApi(data);
};

const getRecaptcha = async () => {
  try {
    const finalKey = await Utils.getV3Recaptcha();
    const recaptcha = await load(`${finalKey}`, {
      autoHideBadge: true,
    });
    const token = await recaptcha.execute("submit");
    return token;
  } catch (error: any) {
    console.log(error);
  }
};

const forgotPasswordApi = async (data: any) => {
  try {
    const recaptcha = await getRecaptcha();
    const res: any = await userService.forgotPassword(
      data.emailForgotPassword,
      recaptcha,
    );
    secret.value = res.data.data.secret;
    showToastMessage(res.data.furtherDetails, ToastVariant.SUCCESS);
  } catch (error: any) {
    isLoading.value = false;
    if (error && error.data && error.data.error && error.data.error.type) {
      if (error.data.error.type === "exc_session_invalid-1") {
        sessionStorage.setItem(
          "prospectCallbackErrorInfo",
          JSON.stringify(error.data.error),
        );
        router.push({ path: "/" });
      } else {
        showToastMessage(error.data.error.message, ToastVariant.ERROR);
      }
    } else {
      showErrorInfo(error.data);
    }
  }
};
const showErrorInfo = (errorInfo, stateIsPositiveNotification = false) => {
  if (errorInfo && errorInfo !== "") {
    isPositiveNotification.value = stateIsPositiveNotification;
    errorInfo =
      typeof errorInfo === "string" ? JSON.parse(errorInfo) : errorInfo;

    if (errorInfo && Object.keys(errorInfo).length > 0 && errorInfo.message) {
      showProspectCallbackError.value = true;
      prospectCallbackErrorMsg.value = errorInfo.message;
    }
  }
};

const showToastMessage = (message: string, variant: ToastVariant) => {
  const emitData: IToastMessage = {
    variant,
    message,
  };
  eventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};

const onClickClose = () => {
  router.go(-1);
};

const beforeUnmount = () => {
  if (document.body.classList.contains("page-opacity")) {
    document.body.classList.remove("page-opacity", "page-bg", "page-shadow");
  }
};

onMounted(() => {
  onResize();
  window.addEventListener("resize", onResize);
  if (!document.body.classList.contains("page-opacity")) {
    onResize();
    document.body.classList.add("page-opacity", "page-bg");
  }
  emailPlaceholder.value = getUiText("app-user-login-username-prompt-1");
});

onBeforeUnmount(() => {
  beforeUnmount();
  window.removeEventListener("resize", onResize);
});
</script>

<style lang="scss" scoped>
.password-reset-page {
  background: #0345bd;

  .form-control:focus {
    box-shadow: none;
  }
  .form-control::placeholder {
    color: #b7b7b7;
    font-size: 22px;
  }

  .password-reset-modal {
    height: auto;
    width: auto;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    .modal-dialog {
      transition: none;
      min-width: 630px;
    }
    .modal-header {
      justify-content: flex-end;
      border: none;
      padding: 10px 10px 0px 10px;

      .close {
        float: right;
        opacity: 1;
        margin-right: 10px;
        margin-top: 10px;
        position: relative;
        padding: 0;
        z-index: 999;

        img {
          width: 19px;
          height: 19px;
        }
      }
    }

    .modal-content {
      box-shadow: 2px 0px 20px rgba(33, 31, 88, 0.1);
      border-radius: 6px;
      border: none;
    }

    .modal-body {
      padding: 35px 195px 48px 50px;

      .flex-container {
        position: absolute;
        bottom: 0px;
        height: 100%;
      }

      .modal-body-sec1 {
        color: #0345bd;
        font-size: 26px;
        font-weight: 600;
        line-height: 39px;
      }

      .letsgo-btn {
        margin-top: 38px;
        display: flex;
        align-items: center;
      }

      .input-label-form {
        font-weight: 500;
        font-size: 12px;
        color: #231f20;
        line-height: 18px;
      }
      .form-input {
        border: none;
        font-style: normal;
        font-weight: 300;
        font-size: 22px;
        line-height: 33px;
        color: #0345bd;
        border-left: 1px solid rgba(183, 183, 183, 0.3);
        border-radius: unset;
      }
      .form-group {
        margin-top: 15px;

        label {
          margin-bottom: 14px;
          margin-top: 14px;
        }
      }

      .password-left-conditions {
        margin-bottom: 0px;
      }

      .letsgo-button {
        width: 208px;
        height: 45px;
        left: 72px;
        border-radius: 29.4638px;
        background: #0345bd;
        font-style: normal;
        font-weight: 600;
        font-size: 18px;
        box-shadow: 0px 2px 4px rgba(33, 31, 88, 0.25);
        line-height: 27px;
        text-transform: uppercase;
      }

      .letsgo-button:disabled {
        background: #b7b7b7;
        color: #fff;
        border: none;
      }

      .help-text {
        font-size: 13px;
        line-height: 19px;
        color: #878787;
      }
    }
  }
  .modal {
    z-index: 1 !important;
  }
}
@media (max-width: 767px) {
  .password-reset-page {
    .password-reset-modal {
      .modal-header {
        .close {
          margin-top: 3px !important;
          img {
            width: 17px;
            height: 17px;
          }
        }
      }

      .modal-dialog {
        min-width: unset;
      }
      .modal-body {
        padding: 18px 30px 30px 30px;

        .modal-body-sec1 {
          font-size: 22px;
        }

        .form-group {
          margin-top: 24px !important;
        }
        .letsgo-button {
          width: 136px;
          font-size: 16px;
          line-height: 24px;
        }
        .form-input {
          font-size: 18px;
          padding-right: 0px;
        }
        .form-input::placeholder {
          font-size: 18px;
        }

        .input-label-form {
          font-size: 12px;
          line-height: 18px;
        }

        .letsgo-btn {
          margin-top: 20px;
        }
      }
    }
  }
}

@media (max-height: 500px) {
  .modal {
    padding-top: 50px;
  }
}
@media (min-width: 3840px) {
  .password-reset-page {
    .password-reset-modal {
      .form-control:focus {
        padding-left: 30px;
        font-size: 38px;
        line-height: 30px;
      }
      .modal-dialog {
        max-width: 1090px !important;
      }
      .modal-body {
        padding: 60px 230px 90px 90px;
      }
      .modal-content {
        border-radius: 15px !important;
      }
      .modal-body-sec1 {
        font-size: 52px !important;
        line-height: 78px !important;
      }
      .form-group {
        margin-top: 35px !important;
      }
      .input-label-form {
        font-size: 24px !important;
        line-height: 36px !important;
        margin-bottom: 28px !important;
      }
      .close {
        margin-right: 35px !important;
        margin-top: 40px !important;

        img {
          height: 30px !important;
          width: 30px !important;
          zoom: 1.2;
        }
      }

      .letsgo-btn {
        margin-top: 76px !important;
      }
      .form-input {
        font-size: 44px !important;
        padding-left: 30px;
        border-left: 2px solid rgba(183, 183, 183, 0.3) !important;
      }
      .form-input::placeholder {
        font-size: 44px !important;
      }

      .letsgo-button {
        width: 416px !important;
        height: 90px !important;
        font-size: 30px !important;
        border-radius: 41px !important;
      }
      .help-text {
        padding-top: 20px !important;
        //padding-right: 150px;
        line-height: 38px !important;
        font-size: 26px !important;
      }
    }
  }
}
</style>
